// ============================================================
// SkyPlus Sector Service
// app/components/skyplus/shared/sector/sector.service.ts
//
// Single source of truth for tenant mode, feature flags,
// active pack vocabulary, and brand identity. Mirrors the
// MenuService pattern - loaded once at boot, exposed via
// BehaviorSubjects, read via synchronous getters in templates.
//
// PUBLIC API:
//   sector.mode                 - 'client' | 'dev' | 'demo'
//   sector.feature('aiChat')    - true/false
//   sector.label('caseFile')    - vocabulary lookup
//   sector.pack                 - active Pack object
//   sector.brand                - active Brand object (or null)
//   sector.applyBrand()         - apply brand colours + favicon + title
//   sector.canSwitchPack        - true only in demo mode
//   sector.isAdminView          - true in dev or demo
// ============================================================

import { Injectable } from '@angular/core';
import { AppConfigService } from '@alfresco/adf-core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  Pack,
  TenantResponse,
  TenantMode,
  FeatureFlags,
  DEFAULT_FEATURES,
  DEFAULT_BRAND,
  PackSummary,
  VocabKey,
  CaseFileType,
  Brand,
  configBrand
} from './sector.model';
import { GENERIC_PACK } from './packs/generic.pack';

@Injectable({ providedIn: 'root' })
export class SectorService {

  private readonly _tenantMode$    = new BehaviorSubject<TenantMode>('client');
  private readonly _features$      = new BehaviorSubject<FeatureFlags>(DEFAULT_FEATURES);
  private readonly _pack$          = new BehaviorSubject<Pack>(GENERIC_PACK);
  private readonly _availablePacks$= new BehaviorSubject<PackSummary[]>([]);
  private readonly _brand$         = new BehaviorSubject<Brand | null>(DEFAULT_BRAND);

  readonly tenantMode$:    Observable<TenantMode>    = this._tenantMode$.asObservable();
  readonly features$:      Observable<FeatureFlags>  = this._features$.asObservable();
  readonly pack$:          Observable<Pack>          = this._pack$.asObservable();
  readonly availablePacks$:Observable<PackSummary[]> = this._availablePacks$.asObservable();
  readonly brand$:         Observable<Brand | null>  = this._brand$.asObservable();

  constructor(private appConfig: AppConfigService) {
    // Before any backend response, paint with the CLIENT brand from
    // app.config.json (falls back to DEFAULT_BRAND only if none configured),
    // so a slow/offline backend never flashes SkyPlus colours.
    this._brand$.next(configBrand(this.appConfig));
  }

  /** Called by TenantLoader at boot, never from components. */
  setFromResponse(response: TenantResponse): void {
    this._tenantMode$.next(response.tenantMode || 'client');
    this._features$.next({ ...DEFAULT_FEATURES, ...(response.features || {}) });
    this._pack$.next(response.pack || GENERIC_PACK);
    this._availablePacks$.next(response.availablePacks || []);
    // Fall back to the CLIENT brand from app.config.json (then DEFAULT_BRAND)
    // rather than SkyPlus, so a backend with no brand keeps the client theme.
    this._brand$.next(response.brand || configBrand(this.appConfig));
  }

  get mode(): TenantMode {
    return this._tenantMode$.value;
  }

  get pack(): Pack {
    return this._pack$.value;
  }

  get features(): FeatureFlags {
    return this._features$.value;
  }

  get brand(): Brand | null {
    return this._brand$.value;
  }

  /**
   * Feature flag check. Unknown flags return false.
   * This is THE gate for every enhanced feature in the app.
   *
   *   Usage in template:  *ngIf="sector.feature('aiChat')"
   *   Usage in TS:        if (this.sector.feature('flipViewer')) ...
   */
  feature(flag: keyof FeatureFlags): boolean {
    return !!this._features$.value?.[flag];
  }

  /**
   * Label lookup with graceful fallback to the generic pack.
   * Never throws - returns the key itself as last resort.
   */
  label(key: VocabKey | string): string {
    const active = this._pack$.value?.vocabulary || {};
    if (active[key]) return active[key];

    const generic = GENERIC_PACK.vocabulary;
    if (generic[key]) return generic[key];

    return key;
  }

  /**
   * Apply brand identity to the document at runtime.
   *
   * Sets CSS custom properties on <html> (--brand-primary, etc.) so any
   * SCSS rule that reads var(--brand-primary) updates without a rebuild.
   * Also sets document.title and the browser favicon if those values are
   * provided in the brand block.
   *
   * Mirrors the SkyPlusLoginComponent.applyBrandColors() pattern so the
   * login screen (pre-auth) and the rest of the app (post-auth) stay
   * visually consistent.
   *
   * Any brand field that is null or empty is SKIPPED - the existing
   * value stays in place. This means a tenant with no brand block
   * produces zero visual change.
   */
  applyBrand(): void {
    const brand = this._brand$.value;
    if (!brand) return;

    const root = document.documentElement;

    if (brand.primaryColor)  root.style.setProperty('--brand-primary',       brand.primaryColor);
    if (brand.primaryDark)   root.style.setProperty('--brand-primary-dark',  brand.primaryDark);
    if (brand.primaryLight)  root.style.setProperty('--brand-primary-light', brand.primaryLight);
    if (brand.accentColor)   root.style.setProperty('--brand-accent',        brand.accentColor);

    if (brand.productName) {
      document.title = brand.productName;
    }

    if (brand.faviconUrl) {
      let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = brand.faviconUrl;
    }
  }

  /**
   * Pack switching is ONLY available in demo mode.
   * Client and dev modes always see canSwitchPack=false.
   */
  get canSwitchPack(): boolean {
    return this._tenantMode$.value === 'demo';
  }

  /**
   * Admin-only UI (pack switcher dropdown, feature-flag debug
   * panel, seed-data tools) shows in dev and demo. Never client.
   */
  get isAdminView(): boolean {
    const m = this._tenantMode$.value;
    return m === 'dev' || m === 'demo';
  }

  caseType(key: string): CaseFileType | null {
    return this._pack$.value?.caseFileTypes?.find(t => t.key === key) || null;
  }

  regulators(): string[] {
    return this._pack$.value?.compliance?.regulators || [];
  }

  retentionFor(docType: string) {
    return this._pack$.value?.compliance?.retentionRules
      ?.find(r => r.docType === docType) || null;
  }
}